import { BlotterTable, DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS, useBlotterTable } from '@talos/kyoko';
import type { ModifyFixingEntry } from './modifyFixingSlice';
import {
  modifyFixingInputID,
  modifyTotalInputID,
  useModifyFixingBlotterColumns,
} from './useModifyFixingBlotterColumns';
import { useModifyFixingBlotterDataObs } from './useModifyFixingBlotterDataObs';

interface ModifyFixingBlotterProps {
  entries: ModifyFixingEntry[];
  onEntryFixingChange: (symbol: string, value: string | undefined) => void;
  onEntryPriceChange: (symbol: string, value: string | undefined) => void;
  onEntryAmountChange: (symbol: string, value: string | undefined) => void;
}

export const ModifyFixingBlotter = ({
  entries,

  onEntryFixingChange,
  onEntryPriceChange,
  onEntryAmountChange,
}: ModifyFixingBlotterProps) => {
  const dataObservable = useModifyFixingBlotterDataObs(entries);
  const columns = useModifyFixingBlotterColumns({
    onEntryFixingChange,
    onEntryPriceChange,
    onEntryAmountChange,
  });

  const blotterTable = useBlotterTable<ModifyFixingEntry>({
    dataObservable,
    errorObservable: undefined,
    rowID: 'symbol' satisfies keyof ModifyFixingEntry,
    applyTransactionsSynchronously: true, // immediate blotter updates since we're doing inline editing
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
      rowHeight: 106, // this height renders the contents (including cell warnings) well within one cell
      suppressHeaderFocus: true,
      focusGridInnerElement: params => {
        // The code in here handles what happens when the user tabs in and out of the blotter. We manually handle the focus so
        // the user is able to tab through the input fields rendered in the blotter
        let rowCount = 0;
        params.api.forEachNode(() => rowCount++);
        const topLeftInput = modifyFixingInputID(0);
        const bottomRightInput = modifyTotalInputID(rowCount - 1);
        const inputElement = document.getElementById(
          params.fromBottom ? bottomRightInput : topLeftInput
        ) as HTMLInputElement | null;
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }

        return true;
      },
    },
    columns,
  });

  return <BlotterTable {...blotterTable} />;
};
